// A bottom sheet on a phone (drag the handle down to dismiss), a centred
// dialog on a desk. Built on <dialog>, so the focus trap, the inert page
// behind, Escape and the top layer come from the browser.
import { useEffect, useId, useRef, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { cx } from './cx';
import { isDesk } from './viewport';

export interface SheetProps {
  open: boolean;
  /** Told whenever the sheet closes, by whichever route: Escape, the scrim, a drag, or a button inside. */
  onClose: () => void;
  title?: string;
  /** Its name when there is no title to read. */
  label?: string;
  /** Most of the screen, for a drawer. */
  full?: boolean;
  className?: string;
  children: ReactNode;
}

const DISMISS_PX = 120, DISMISS_SPEED = 0.6;

export function Sheet({ open, onClose, title, label, full, className, children }: SheetProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      // Browsers no longer focus a descendant on their own (and autofocus is
      // the wrong tool: it fires on page load too).
      d.querySelector<HTMLElement>('.sheet-body :is(input, textarea, select, button, [tabindex]):not([disabled])')?.focus();
    } else if (!open && d.open) d.close();
  }, [open]);

  // The sheet follows the finger from the handle and springs back unless it
  // went far or fast. Transform only.
  // A press that starts and ends on the scrim (the dialog's own box, since
  // the content fills it) closes the sheet.
  const onScrim = useRef(false);
  const scrimDown = (e: ReactPointerEvent<HTMLDialogElement>) => { onScrim.current = e.target === e.currentTarget; };
  const scrimUp = (e: ReactPointerEvent<HTMLDialogElement>) => { if (onScrim.current && e.target === e.currentTarget) onClose(); onScrim.current = false; };

  const drag = useRef<{ y: number; t: number; dy: number } | null>(null);
  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (isDesk()) return;
    drag.current = { y: e.clientY, t: performance.now(), dy: 0 };
    e.currentTarget.setPointerCapture(e.pointerId);
    ref.current?.classList.add('dragging');
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current, el = ref.current;
    if (!d || !el) return;
    d.dy = Math.max(0, e.clientY - d.y);
    el.style.transform = `translateY(${d.dy}px)`;
  };
  const onPointerUp = () => {
    const d = drag.current, el = ref.current;
    if (!d || !el) return;
    drag.current = null;
    el.classList.remove('dragging');
    el.style.transform = '';
    if (d.dy > DISMISS_PX || d.dy / Math.max(1, performance.now() - d.t) > DISMISS_SPEED) onClose();
  };

  return (
    <dialog
      ref={ref} className={cx('sheet', full && 'full', className)}
      aria-labelledby={title ? titleId : undefined} aria-label={title ? undefined : label}
      onClose={onClose} onPointerDown={scrimDown} onPointerUp={scrimUp}
    >
      <div className="sheet-inner">
        <div className="sheet-handle" onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}>
          <span className="grabber" />
        </div>
        {title && <h2 id={titleId} className="sheet-title">{title}</h2>}
        <div className="sheet-body">{children}</div>
      </div>
    </dialog>
  );
}
